import { Injectable, signal } from '@angular/core';
import { ResolveIntentResponse } from './assist.model';
import { assistantSafeText } from '../account-identifier-presentation';

export type AssistHistoryOutcome =
  | 'prepared-navigated-to-form'
  | 'executed-read'
  | 'declined'
  | 'clarify';

export interface AssistHistoryEntry {
  readonly id: string;
  readonly command: string;
  readonly actionKind: string;
  readonly naturalLanguageSummary: string;
  readonly outcome: AssistHistoryOutcome;
  readonly timestamp: string;
  /**
   * Track 5 leg 2 (re-run): the ORIGINAL user-typed command when it was remembered via
   * rememberCommand(), else null. `command` above falls back to the server summary/action
   * kind for display, which is NOT re-submittable — the history drawer only offers
   * "Re-run" when this field is present.
   */
  readonly rerunCommand: string | null;
}

const MAX_HISTORY_ENTRIES = 12;

function newHistoryId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `assist-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

@Injectable({ providedIn: 'root' })
export class AssistHistoryService {
  private readonly commandByResponse = new WeakMap<ResolveIntentResponse, string>();
  private readonly entryIdByResponse = new WeakMap<ResolveIntentResponse, string>();
  private readonly _entries = signal<AssistHistoryEntry[]>([]);

  readonly entries = this._entries.asReadonly();

  rememberCommand(command: string, response: ResolveIntentResponse): void {
    const trimmed = command.trim();
    if (trimmed) {
      this.commandByResponse.set(response, trimmed);
    }
  }

  /**
   * The original user-typed command for a response, when known (rememberCommand was called
   * with it). Track 5: the shell uses this to restore the text on "Rephrase".
   */
  commandFor(response: ResolveIntentResponse): string | null {
    return this.commandByResponse.get(response) ?? null;
  }

  recordOutcome(response: ResolveIntentResponse, outcome: AssistHistoryOutcome): AssistHistoryEntry {
    const id = this.entryIdByResponse.get(response) ?? newHistoryId();
    this.entryIdByResponse.set(response, id);
    const remembered = this.commandByResponse.get(response) ?? null;
    const entry: AssistHistoryEntry = {
      id,
      command: remembered
        ?? assistantSafeText(response.naturalLanguageSummary, response.params)
        ?? response.actionKind,
      actionKind: response.actionKind,
      naturalLanguageSummary: assistantSafeText(response.naturalLanguageSummary, response.params),
      outcome,
      timestamp: new Date().toISOString(),
      rerunCommand: remembered,
    };

    this._entries.update((entries) => [
      entry,
      ...entries.filter((existing) => existing.id !== id),
    ].slice(0, MAX_HISTORY_ENTRIES));
    return entry;
  }

  clear(): void {
    this._entries.set([]);
  }
}
